import { useCallback } from 'react';
import axios from 'axios';
import { FormikValues } from 'formik';
import { QuestionnaireData } from '@contracts';
import getOmsUrl from '@utils/getOmsUrl';
import { unescapeDeep } from '@utils/html';

type UseInsightHook = () => {
  createInsight: (questionnaireRevisionId: string, googleIdToken: string, insightData: FormikValues, completed: boolean) => any;
  getInsightProgress: (questionnaireRevisionId: string, googleIdToken: string) => any;
  getCompletedInsight: (questionnaireRevisionId: string, googleIdToken: string) => any;
  saveInsightProgress: (insightId: string, googleIdToken: string, body: FormikValues) => any;
};

export const manageHealth = () => {
  const baseUrl = '/health';
  const omsUrl = getOmsUrl();

  const useGetQuestionnaire = (): { getQuestionnaire: (id: string, googleIdToken: string) => Promise<QuestionnaireData> } => {
    const getQuestionnaire = useCallback(async (id: string, googleIdToken: string | undefined) => {
      const url = `${omsUrl}${baseUrl}/questionnaires/${id}`;
      const headers = {
        token: googleIdToken ?? '',
        'Content-Type': 'application/json'
      };
      const res = await axios.get(url, { headers });
      const unescapedData = unescapeDeep(res.data);

      return unescapedData as QuestionnaireData;
    }, []);

    return { getQuestionnaire };
  };

  const useActivateKit = () => {
    const activateKit = useCallback(async (kitId: string, googleIdToken: string, body = {}) => {
      const url = `${omsUrl}${baseUrl}/results/activation/${kitId}`;
      const headers = {
        token: googleIdToken ?? '',
        'Content-Type': 'application/json'
      };
      const res = await axios.put(url, body, { headers });
      return res.data;
    }, []);

    return { activateKit };
  };

  const useInsight: UseInsightHook = () => {
    const createInsight = useCallback(
      async (questionnaireRevisionId: string, googleIdToken: string, insightData: FormikValues, completed: boolean) => {
        const url = `${omsUrl}${baseUrl}/insights/${questionnaireRevisionId}`;
        const headers = {
          token: googleIdToken ?? '',
          'Content-Type': 'application/json'
        };
        const body = {
          data: insightData,
          completed: completed ?? false
        };
        const res = await axios.post(url, body, { headers });
        return res.data;
      },
      []
    );

    const getInsightProgress = useCallback(async (questionnaireRevisionId: string, googleIdToken: string) => {
      const url = `${omsUrl}${baseUrl}/insights/${questionnaireRevisionId}`;
      const headers = {
        token: googleIdToken ?? '',
        'Content-Type': 'application/json'
      };
      const res = await axios.get(url, { headers });
      return res.data;
    }, []);

    const getCompletedInsight = useCallback(async (questionnaireRevisionId: string, googleIdToken: string) => {
      const url = `${omsUrl}${baseUrl}/insights/${questionnaireRevisionId}/completed`;
      const headers = {
        token: googleIdToken ?? '',
        'Content-Type': 'application/json'
      };
      const res = await axios.get(url, { headers });
      return res.data;
    }, []);

    const saveInsightProgress = useCallback(async (insightId: string, googleIdToken: string, body: FormikValues) => {
      const url = `${omsUrl}${baseUrl}/insights/${insightId}`;
      const headers = {
        token: googleIdToken ?? '',
        'Content-Type': 'application/json'
      };
      const data = JSON.stringify(body);
      const res = await axios.put(url, data, { headers });
      return res.data;
    }, []);

    return { createInsight, getInsightProgress, getCompletedInsight, saveInsightProgress };
  };

  return { useGetQuestionnaire, useInsight, useActivateKit };
};
